import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select,
    OutlinedInput,
    Chip,
    Box,
    InputAdornment,
} from '@mui/material';
import { useState, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import { CREATE_PACKAGE, EDIT_PACKAGE } from 'src/graphql/mutations/packages';
import { useSnackbar } from 'notistack';

const PACKAGE_TYPES = [
    { value: 'BRONZE BASIC', label: 'Bronze Basic' },
    { value: 'SILVER STANDARD', label: 'Silver Standard' },
    { value: 'GOLD PREMIUM', label: 'Gold Premium' },
    { value: 'PLATINUM DELUXE', label: 'Platinum Deluxe' },
];

export default function PackageDialog({ open, onClose, package: pkg, products, refetch }) {
    const { enqueueSnackbar } = useSnackbar();
    const [formData, setFormData] = useState({
        packageType: '',
        amount: '',
        products: [],
    });

    const [createPackage, { loading: createLoading }] = useMutation(CREATE_PACKAGE, {
        onCompleted: () => {
            enqueueSnackbar('Package created successfully!', { variant: 'success' });
            refetch();
            handleClose();
        },
        onError: (error) => {
            enqueueSnackbar(error.message || 'Failed to create package', { variant: 'error' });
        },
    });

    const [editPackage, { loading: editLoading }] = useMutation(EDIT_PACKAGE, {
        onCompleted: () => {
            enqueueSnackbar('Package updated successfully!', { variant: 'success' });
            refetch();
            handleClose();
        },
        onError: (error) => {
            enqueueSnackbar(error.message || 'Failed to update package', { variant: 'error' });
        },
    });

    useEffect(() => {
        if (pkg) {
            setFormData({
                packageType: pkg.packageType || '',
                amount: pkg.amount || '',
                products: pkg.products?.map((p) => p._id) || [],
            });
        } else {
            setFormData({
                packageType: '',
                amount: '',
                products: [],
            });
        }
    }, [pkg, open]);

    const handleClose = () => {
        setFormData({
            packageType: '',
            amount: '',
            products: [],
        });
        onClose();
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleSubmit = () => {
        if (!formData.packageType || !formData.amount) {
            enqueueSnackbar('Please fill in all required fields', { variant: 'warning' });
            return;
        }

        const input = {
            packageType: formData.packageType,
            amount: parseFloat(formData.amount),
            products: formData.products,
        };

        if (pkg) {
            editPackage({
                variables: {
                    input: {
                        packageId: pkg._id,
                        ...input,
                    },
                },
            });
        } else {
            createPackage({ variables: { input } });
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{pkg ? 'Edit Package' : 'Create New Package'}</DialogTitle>
            <DialogContent>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
                    <FormControl fullWidth required>
                        <InputLabel>Package Type</InputLabel>
                        <Select
                            name="packageType"
                            value={formData.packageType}
                            onChange={handleChange}
                            label="Package Type"
                        >
                            {PACKAGE_TYPES.map((type) => (
                                <MenuItem key={type.value} value={type.value}>
                                    {type.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    <TextField
                        fullWidth
                        required
                        label="Amount"
                        name="amount"
                        type="number"
                        value={formData.amount}
                        onChange={handleChange}
                        InputProps={{
                            startAdornment: <InputAdornment position="start">₦</InputAdornment>,
                        }}
                    />

                    <FormControl fullWidth>
                        <InputLabel>Products</InputLabel>
                        <Select
                            multiple
                            name="products"
                            value={formData.products}
                            onChange={handleChange}
                            input={<OutlinedInput label="Products" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => {
                                        const product = products?.find((p) => p._id === value);
                                        return <Chip key={value} label={product?.productName || value} size="small" />;
                                    })}
                                </Box>
                            )}
                        >
                            {products
                                ?.filter((p) => p.isAddOn)
                                ?.map((product) => (
                                    <MenuItem key={product._id} value={product._id}>
                                        {product.productName} (₦{product.amount?.toLocaleString()})
                                    </MenuItem>
                                ))}
                        </Select>
                    </FormControl>
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    disabled={createLoading || editLoading}
                >
                    {pkg ? 'Update' : 'Create'}
                </Button>
            </DialogActions>
        </Dialog>
    );
}
